import { DatePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  input,
  signal,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { AgencyDto, AgencyUserDto } from '@/app/common/general-types/agency.dto';
import { UserRolesEnum } from '@/app/common/general-types/users.dto';
import { ImageService } from '@/app/services/image/image.service';
import { TooltipDirective } from '@/app/common/directives/tooltip/tooltip.directive';
import { FirebaseService } from '@/app/common/firebase/firebase.service';
import { CurrentUserService } from '@/app/common/current-user/current-user.service';
import { AgencyService } from '@/app/services/agency/agency.service';
import { catchError, distinctUntilChanged, map, of, switchMap, tap } from 'rxjs';

type TeamLoadResult = {
  status: 'idle' | 'loaded' | 'error';
  users: AgencyUserDto[];
};

@Component({
  selector: 'agency-ins-team',
  imports: [DatePipe, TooltipDirective],
  templateUrl: './team.html',
  host: { class: 'block h-full w-full min-h-0' },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class Team {
  readonly agency = input<AgencyDto | undefined>(undefined);

  readonly imageService = inject(ImageService);
  readonly firebaseService = inject(FirebaseService);
  readonly currentUserService = inject(CurrentUserService);
  private readonly agencyService = inject(AgencyService);
  private readonly destroyRef = inject(DestroyRef);

  members = signal<AgencyUserDto[]>([]);
  loadingState = signal<'idle' | 'loading' | 'loaded' | 'error'>('idle');

  pendingImpersonation = signal<{ uid: string; displayName: string } | null>(null);
  apiKeyInput = signal('');
  isLoading = signal(false);
  impersonateError = signal<string | null>(null);

  constructor() {
    toObservable(this.agency)
      .pipe(
        distinctUntilChanged((previous, current) => previous?.id === current?.id),
        tap((agency) => {
          this.members.set([]);
          this.loadingState.set(agency?.id ? 'loading' : 'idle');
        }),
        switchMap((agency) => {
          if (!agency?.id) {
            return of<TeamLoadResult>({ status: 'idle', users: [] });
          }
          return this.agencyService.getAgencyUsers(agency.id).pipe(
            map((users): TeamLoadResult => ({ status: 'loaded', users: users ?? [] })),
            catchError((err) => {
              console.error('Error fetching agency team members:', err);
              return of<TeamLoadResult>({ status: 'error', users: [] });
            }),
          );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((result) => {
        this.members.set(result.users);
        this.loadingState.set(result.status);
      });
  }

  teamMembers(): AgencyUserDto[] {
    return this.members().filter((member) => !member.isDeleted);
  }

  userRoleLabel(role: number | undefined): string {
    if (role === undefined || role === null) {
      return '—';
    }
    const label = (UserRolesEnum as Record<number, string>)[role];
    return label ?? `Role ${role}`;
  }

  memberDisplayName(member: AgencyUserDto): string {
    const user = member.userAccount;
    if (!user) {
      return '—';
    }
    const name = `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim();
    if (name) {
      return name;
    }
    return user.email ?? '—';
  }

  triggerImpersonation(uid: string, displayName: string): void {
    this.pendingImpersonation.set({ uid, displayName });
    this.apiKeyInput.set('');
    this.impersonateError.set(null);
  }

  async confirmImpersonation(): Promise<void> {
    const pending = this.pendingImpersonation();
    if (!pending || !this.apiKeyInput()) return;
    this.isLoading.set(true);
    this.impersonateError.set(null);
    try {
      const preferredBrandId = this.agency()?.businessAccounts?.[0]?.id;
      await this.firebaseService.impersonateFromApp(
        pending.uid,
        pending.displayName,
        this.apiKeyInput(),
        preferredBrandId,
      );
      this.pendingImpersonation.set(null);
      this.apiKeyInput.set('');
    } catch {
      this.impersonateError.set('Failed to impersonate. Check your API key and try again.');
    } finally {
      this.isLoading.set(false);
    }
  }

  cancelImpersonation(): void {
    this.pendingImpersonation.set(null);
    this.apiKeyInput.set('');
    this.impersonateError.set(null);
  }
}
